import React, { useRef } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { sanitize } from "dompurify";
import * as yup from "yup";

import "../../assets/css/majorybl/pages/contact.scss";
import { alertActions } from "../../features/alert/alertSlice";
import {
    selectAgeLevels,
    selectSetting,
} from "../../features/global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { registerUser, verifyEmail } from "../../lib/api";
import { PHONE_REGEXP } from "../../lib/globals";
import { displayFormAlert } from "../../lib/utils";
import { IAgeLevel } from "../../types/api";
import { IRegisterCoach } from "../../types/forms";

const RegisterCoach = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const recaptcha = useRef<ReCAPTCHA>(null);
    const pageText = useAppSelector(state =>
        selectSetting(state, "majorybl_coach_registration_content"),
    );
    const ageLevels = useAppSelector(selectAgeLevels);

    const registerSchema = yup.object().shape({
        firstName: yup.string().required("First Name is required"),
        lastName: yup.string().required("Last Name is required"),
        email: yup
            .string()
            .required("Email address is required")
            .email("Email is invalid"),
        confirmEmail: yup
            .string()
            .required("Please confirm your email address")
            .email("Email is invalid")
            .oneOf([yup.ref("email")], "Email does not match"),
        phone: yup
            .string()
            .required("Phone number is required")
            .matches(PHONE_REGEXP, "Phone number is not valid"),
        password: yup
            .string()
            .required("Please choose a password")
            .min(8, "Password length should be at least 8 characters"),
        confirmPassword: yup
            .string()
            .required("Please confirm your password")
            .min(8, "Password length should be at least 8 characters")
            .oneOf([yup.ref("password")], "Passwords do not match"),
        recaptcha: yup.string().nullable(),
        teamName: yup.string().required("Team Name is required"),
        ageLevel: yup
            .mixed<IAgeLevel["ageLevel"]>()
            .required("Age Level is required")
            .oneOf(
                ageLevels.map(a => a.ageLevel) as IAgeLevel["ageLevel"][],
                "Invalid Age Level selected",
            ),
    });

    const verifyCaptcha = (): boolean => {
        const recaptchaValue = recaptcha.current?.getValue();
        if (!recaptchaValue) {
            setError("recaptcha", {
                type: "custom",
                message: "Please verify the reCAPTCHA!",
            });
            return false;
        } else {
            clearErrors("recaptcha");
            return true;
        }
    };

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        setValue,
        formState: { errors },
    } = useForm({
        mode: "onChange",
        resolver: yupResolver(registerSchema),
    });

    const onSubmit: SubmitHandler<IRegisterCoach> = async data => {
        try {
            await verifyEmail(data.email);
        } catch (e) {
            setError("email", {
                type: "custom",
                message: "The specified email is already registered",
            });
            return false;
        }

        if (verifyCaptcha()) {
            await registerUser(data, "coach")
                .then(() => {
                    dispatch(
                        alertActions.success({
                            title: "Registration Successful",
                            text: "Thank you for registering. Once your account has been assigned to your team, you will have access to it within the Coach's Clubhouse.",
                        }),
                    );
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem registering your account.`,
                            text: "If the problem continues, please reach out to an admin.",
                            dismissible: true,
                        }),
                    );
                });
        }
    };

    const onError: SubmitErrorHandler<IRegisterCoach> = (): void => {
        verifyCaptcha();
    };

    const onChangeRecaptcha = async (token: string | null): Promise<void> => {
        const recaptchaValue = recaptcha.current?.getValue();
        setValue("recaptcha", recaptchaValue);
        verifyCaptcha();
        // TODO: Verify response by sending token to back-end API
    };

    return (
        <Form
            onSubmit={handleSubmit(onSubmit, onError)}
            id="registerForm"
            data-testid="registerCoachForm"
        >
            <Row>
                <Col>
                    <h2>Coach Registration</h2>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div
                        className="no-bottom-pad"
                        dangerouslySetInnerHTML={{
                            __html: sanitize(pageText?.setting),
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <Form.Label>Name *</Form.Label>
                <Form.Group as={Col} controlId="firstName">
                    <Form.Control
                        type="text"
                        {...register("firstName")}
                        aria-invalid={errors.firstName ? "true" : "false"}
                    />
                    <Form.Label>First</Form.Label>
                    {errors.firstName &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.firstName.message as string,
                        })}
                </Form.Group>
                <Form.Group as={Col} controlId="lastName">
                    <Form.Control
                        type="text"
                        {...register("lastName")}
                        aria-invalid={errors.lastName ? "true" : "false"}
                    />
                    <Form.Label>Last</Form.Label>
                    {errors.lastName &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.lastName.message as string,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group controlId="teamName">
                        <Form.Label>Team Name *</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("teamName")}
                            aria-invalid={errors.teamName ? "true" : "false"}
                        />
                        {errors.teamName &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.teamName.message as string,
                            })}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="ageLevel">
                        <Form.Label>Age *</Form.Label>
                        <Form.Select
                            defaultValue=""
                            {...register("ageLevel")}
                            aria-invalid={errors.ageLevel ? "true" : "false"}
                        >
                            <option disabled value="">
                                Choose Age
                            </option>
                            {ageLevels.map(a => (
                                <option value={a.ageLevel} key={a.id}>
                                    {a.ageLevel}
                                </option>
                            ))}
                        </Form.Select>
                        {errors.ageLevel &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.ageLevel.message as string,
                            })}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Form.Label>Email *</Form.Label>
                <Form.Group as={Col} controlId="email">
                    <Form.Control
                        type="email"
                        {...register("email")}
                        aria-invalid={errors.email ? "true" : "false"}
                    />
                    <Form.Label>Enter Email</Form.Label>
                    {errors.email &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.email.message as string,
                        })}
                </Form.Group>
                <Form.Group as={Col} controlId="confirmEmail">
                    <Form.Control
                        type="email"
                        {...register("confirmEmail")}
                        aria-invalid={errors.confirmEmail ? "true" : "false"}
                    />
                    <Form.Label>Confirm Email</Form.Label>
                    {errors.confirmEmail &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.confirmEmail.message as string,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group controlId="phone">
                        <Form.Label>Phone *</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("phone")}
                            aria-invalid={errors.phone ? "true" : "false"}
                        />
                        {errors.phone &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.phone.message as string,
                            })}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Form.Label>Password *</Form.Label>
                <Form.Group as={Col} controlId="password">
                    <Form.Control
                        type="password"
                        {...register("password")}
                        aria-invalid={errors.password ? "true" : "false"}
                    />
                    <Form.Label>Enter Password</Form.Label>
                    {errors.password &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.password.message as string,
                        })}
                </Form.Group>
                <Form.Group as={Col} controlId="confirmPassword">
                    <Form.Control
                        type="password"
                        {...register("confirmPassword")}
                        aria-invalid={errors.confirmPassword ? "true" : "false"}
                    />
                    <Form.Label>Confirm Password</Form.Label>
                    {errors.confirmPassword &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.confirmPassword.message as string,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Col xs={3}>
                    <Form.Label>CAPTCHA</Form.Label>
                    <ReCAPTCHA
                        ref={recaptcha}
                        sitekey={process.env.RECAPTCHA_SITE_KEY as string}
                        onChange={onChangeRecaptcha}
                    />
                    {errors.recaptcha &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.recaptcha.message as string,
                        })}
                </Col>
            </Row>
            <Row>
                <Col>
                    <Button variant="primary" type="submit">
                        Submit
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default RegisterCoach;
